/**
 * Матрица prompt × answer и правила подсказок/транскрипции — зеркало бэкенда TestPromptAnswerMatrix.
 * Форматы ответа: typed | speech | choose.
 */

export const PROMPT_AUDIO = 'audio'
export const PROMPT_SOURCE_TEXT = 'source_text'
export const PROMPT_TRANSLATION_TEXT = 'translation_text'

export const ANSWER_TYPED = 'typed'
export const ANSWER_SPEECH = 'speech'
export const ANSWER_CHOOSE = 'choose'

export const SPEECH_ANSWER_TYPES = [ANSWER_SPEECH] as const

export type SkillType =
  | 'translation'
  | 'reverse_translation'
  | 'spelling'
  | 'listening'
  | 'pronunciation'

export function normalizeSkillType(skill: string): string {
  return skill.trim()
}

export function normalizeAnswerType(answerType: string): string {
  return answerType.trim()
}

export function allowedAnswerTypes(promptType: string): string[] {
  const matrix: Record<string, string[]> = {
    [PROMPT_AUDIO]: [ANSWER_TYPED, ANSWER_CHOOSE, ANSWER_SPEECH],
    [PROMPT_SOURCE_TEXT]: [ANSWER_TYPED, ANSWER_SPEECH, ANSWER_CHOOSE],
    [PROMPT_TRANSLATION_TEXT]: [ANSWER_TYPED, ANSWER_CHOOSE, ANSWER_SPEECH],
  }
  return matrix[promptType] ?? []
}

export function isPairAllowed(promptType: string, answerType: string): boolean {
  return allowedAnswerTypes(promptType).includes(normalizeAnswerType(answerType))
}

/** Порядок навыков в UI и в `enabled_skill_type` (согласован с бэкендом). */
export const SKILL_DISPLAY_ORDER: readonly SkillType[] = [
  'translation',
  'reverse_translation',
  'spelling',
  'listening',
  'pronunciation',
]

/** Все навыки — для расчёта пула `answer_type` без ручного выбора целей в форме урока. */
export const ALL_SKILL_TYPES: readonly SkillType[] = SKILL_DISPLAY_ORDER

/**
 * Навыки, отражаемые ответом для пары (prompt_type, answer_type) — зеркало
 * `TestPromptAnswerMatrix::evaluatedSkillTypes`.
 */
export function evaluatedSkillTypes(promptType: string, answerType: string): string[] {
  const at = normalizeAnswerType(answerType)
  if (!isPairAllowed(promptType, at)) {
    return []
  }

  if (promptType === PROMPT_SOURCE_TEXT) {
    if (at === ANSWER_TYPED || at === ANSWER_CHOOSE) {
      return ['translation']
    }
    if (at === ANSWER_SPEECH) {
      return ['translation', 'pronunciation']
    }

    return []
  }

  if (promptType === PROMPT_TRANSLATION_TEXT) {
    if (at === ANSWER_TYPED) {
      return ['reverse_translation', 'spelling']
    }
    if (at === ANSWER_SPEECH) {
      return ['pronunciation', 'reverse_translation']
    }
    if (at === ANSWER_CHOOSE) {
      return ['reverse_translation']
    }

    return []
  }

  if (promptType === PROMPT_AUDIO) {
    if (at === ANSWER_TYPED) {
      return ['listening', 'spelling']
    }
    if (at === ANSWER_CHOOSE) {
      return ['listening']
    }
    if (at === ANSWER_SPEECH) {
      return ['listening', 'translation']
    }

    return []
  }

  return []
}

/**
 * Цели проверки по карточке с учётом списка «включённых» навыков — зеркало
 * `TestPromptAnswerMatrix::evaluatedSkillTypesForAnswerRows`.
 */
export function evaluatedSkillTypesForAnswerRows(
  promptType: string,
  answerType: string,
  itemEnabledSkills: string[] | null | undefined,
): string[] {
  const full = evaluatedSkillTypes(promptType, answerType)
  const enabled = (itemEnabledSkills ?? [])
    .map((s) => normalizeSkillType(String(s).trim()))
    .filter((s) => s.length > 0)
  if (enabled.length === 0) {
    return full
  }
  const enabledSet = new Set(enabled)
  return full.filter((s) => enabledSet.has(normalizeSkillType(s)))
}

function activeSkillTargetsForItem(item: {
  prompt_type: string
  answer_type?: string | null
  active_evaluated_skill_types?: string[] | null
  enabled_skills?: string[] | null
}): string[] {
  const at = normalizeAnswerType(String(item.answer_type ?? '').trim())
  if (item.active_evaluated_skill_types && item.active_evaluated_skill_types.length > 0) {
    return item.active_evaluated_skill_types
  }
  return evaluatedSkillTypesForAnswerRows(item.prompt_type, at, item.enabled_skills)
}

/** Транскрипция ученику: по правилам матрицы и при активной цели «Произношение». */
export function itemAllowsStudentTranscriptionByGoals(item: {
  allow_transcription: boolean
  prompt_type: string
  answer_type?: string | null
  active_evaluated_skill_types?: string[] | null
  enabled_skills?: string[] | null
}): boolean {
  const at = String(item.answer_type ?? '').trim()
  if (!item.allow_transcription || !mayShowTranscription(item.prompt_type, at)) {
    return false
  }
  return activeSkillTargetsForItem(item).some((s) => normalizeSkillType(s) === 'pronunciation')
}

/** Подсказки ученику: по правилам матрицы и при активной цели «Перевод» (`translation`). */ 
export function itemAllowsStudentHintsByGoals(item: {
  allow_hints: boolean
  prompt_type: string
  answer_type?: string | null
  active_evaluated_skill_types?: string[] | null
  enabled_skills?: string[] | null
}): boolean {
  const at = String(item.answer_type ?? '').trim()
  if (!item.allow_hints || !mayShowHints(item.prompt_type, at)) {
    return false
  }
  return activeSkillTargetsForItem(item).some((s) => normalizeSkillType(s) === 'translation')
}

/**
 * Пул `enabled_skill_type` для формы создания теста по выбранным `enabled_prompt_type`
 * и фильтру гранулярных `answer_type`.
 */
export function deriveActiveSkillsFromLessonTestSelection(
  promptTypes: string[],
  granularFilter: string[] | null,
): string[] {
  const set = new Set<string>()
  for (const p of promptTypes) {
    const row = allowedAnswerTypes(p)
    const granular =
      granularFilter === null
        ? row
        : granularFilter.length === 0
          ? []
          : row.filter((at) =>
              granularFilter.some((g) => normalizeAnswerType(g) === normalizeAnswerType(at)),
            )
    for (const at of granular) {
      for (const sk of evaluatedSkillTypes(p, at)) {
        set.add(sk)
      }
    }
  }
  return SKILL_DISPLAY_ORDER.filter((s) => set.has(s))
}

export function mayShowTranscription(promptType: string, answerType: string): boolean {
  if (promptType !== PROMPT_SOURCE_TEXT && promptType !== PROMPT_TRANSLATION_TEXT) return false
  return normalizeAnswerType(answerType) === ANSWER_SPEECH
}

export function mayShowHints(promptType: string, answerType: string): boolean {
  const at = normalizeAnswerType(answerType)
  if (promptType !== PROMPT_SOURCE_TEXT && promptType !== PROMPT_AUDIO) return false
  return [ANSWER_TYPED, ANSWER_SPEECH, ANSWER_CHOOSE].includes(at)
}

export function allowedAnswerTypesForSkill(promptType: string, skillRaw: string): string[] {
  const skill = normalizeSkillType(skillRaw)
  const row = allowedAnswerTypes(promptType)

  let forSkill: string[] = []
  switch (skill) {
    case 'translation':
      if (promptType === PROMPT_SOURCE_TEXT) {
        forSkill = [ANSWER_TYPED, ANSWER_SPEECH, ANSWER_CHOOSE]
      } else if (promptType === PROMPT_AUDIO) {
        forSkill = [ANSWER_SPEECH]
      }
      break
    case 'reverse_translation':
      if (promptType === PROMPT_TRANSLATION_TEXT) {
        forSkill = [ANSWER_TYPED, ANSWER_CHOOSE, ANSWER_SPEECH]
      }
      break
    case 'spelling':
      if (promptType === PROMPT_AUDIO) {
        forSkill = [ANSWER_TYPED]
      } else if (promptType === PROMPT_TRANSLATION_TEXT) {
        forSkill = [ANSWER_TYPED]
      }
      break
    case 'listening':
      if (promptType === PROMPT_AUDIO) {
        forSkill = [ANSWER_TYPED, ANSWER_SPEECH, ANSWER_CHOOSE]
      }
      break
    case 'pronunciation':
      if (promptType === PROMPT_SOURCE_TEXT || promptType === PROMPT_TRANSLATION_TEXT) {
        forSkill = [ANSWER_SPEECH]
      }
      break
    default:
      forSkill = []
  }

  return row.filter((a) => forSkill.includes(a))
}

function intersect(lists: string[][]): string[] {
  if (lists.length === 0) return []
  let acc = lists[0].filter((x, i, a) => a.indexOf(x) === i)
  for (let k = 1; k < lists.length; k++) {
    const set = new Set(lists[k])
    acc = acc.filter((x) => set.has(x))
  }
  return acc
}

export function answerTypesForAllSkills(promptType: string, skills: string[]): string[] {
  const lists = skills.map((s) => allowedAnswerTypesForSkill(promptType, s))
  return intersect(lists)
}

/** Порядок показа в UI. */
export const ANSWER_TYPE_DISPLAY_ORDER: readonly string[] = [
  ANSWER_TYPED,
  ANSWER_SPEECH,
  ANSWER_CHOOSE,
]

export function answerTypesUnionForSkills(promptType: string, skills: string[]): string[] {
  const set = new Set<string>()
  for (const s of skills) {
    for (const at of allowedAnswerTypesForSkill(promptType, s)) {
      set.add(at)
    }
  }
  return ANSWER_TYPE_DISPLAY_ORDER.filter((at) => set.has(at))
}

export function unionAnswerTypesForPromptPool(promptTypes: string[], skills: string[]): string[] {
  const set = new Set<string>()
  for (const p of promptTypes) {
    for (const at of answerTypesUnionForSkills(p, skills)) {
      set.add(at)
    }
  }
  return ANSWER_TYPE_DISPLAY_ORDER.filter((at) => set.has(at))
}

export function isSpeechAnswerType(answerType: string): boolean {
  return normalizeAnswerType(answerType) === ANSWER_SPEECH
}

/** Отображение карточки ученику: зеркало `TestPromptAnswerMatrix::PROMPT_TYPES`. */
export type PromptTypeKey =
  | typeof PROMPT_SOURCE_TEXT
  | typeof PROMPT_TRANSLATION_TEXT
  | typeof PROMPT_AUDIO

export const ANSWER_FORMAT_MODES = ['typed', 'speech', 'choose'] as const
export type AnswerFormatMode = (typeof ANSWER_FORMAT_MODES)[number]

export function answerTypeFormatMode(answerType: string): AnswerFormatMode | null {
  const a = normalizeAnswerType(answerType?.trim() ?? '')
  if (!a) return null
  if (a === ANSWER_TYPED) return 'typed'
  if (a === ANSWER_SPEECH) return 'speech'
  if (a === ANSWER_CHOOSE) return 'choose'
  return null
}

export function compatibleAnswerFormatModes(compatibleGranular: string[]): AnswerFormatMode[] {
  const set = new Set<AnswerFormatMode>()
  for (const at of compatibleGranular) {
    const m = answerTypeFormatMode(at)
    if (m) set.add(m)
  }
  return ANSWER_FORMAT_MODES.filter((m) => set.has(m))
}

export function modeHasCompatibleGranular(
  mode: AnswerFormatMode,
  compatibleGranular: string[],
): boolean {
  return compatibleGranular.some((at) => answerTypeFormatMode(at) === mode)
}

export function granularAnswerTypesFromModes(
  modes: AnswerFormatMode[],
  compatibleGranular: string[],
): string[] {
  if (modes.length === 0) return []
  const modeSet = new Set(modes)
  return compatibleGranular.filter((at) => {
    const m = answerTypeFormatMode(at)
    return m !== null && modeSet.has(m)
  })
}

export function answerFormatModesFromGranular(granular: string[]): AnswerFormatMode[] {
  const set = new Set<AnswerFormatMode>()
  for (const at of granular) {
    const m = answerTypeFormatMode(typeof at === 'string' ? at : '')
    if (m) set.add(m)
  }
  return ANSWER_FORMAT_MODES.filter((m) => set.has(m))
}

export function answerTypeSupportsAutoChecking(answerType: string | null | undefined): boolean {
  const a = normalizeAnswerType(answerType?.trim() ?? '')
  if (!a) return false
  if (a === ANSWER_CHOOSE || a === ANSWER_TYPED) return true
  return false
}
